'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Navbar from '@/components/Navbar';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TRADE_SUPER_CATEGORIES } from '@/app/admin/scraper/data/tradeCategories';
import { MapPin, PoundSterling, ChevronDown, ChevronRight, Sparkles, Star, CheckCircle2, Clock, Zap, Mail, ArrowRight, X, SlidersHorizontal, BadgeCheck, Crown, Send, Heart, AlertCircle, Lock } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'describe' | 'quotes';

type ProviderTier = 'instant' | 'pro' | 'basic';

interface MatchedProvider {
  id: string;
  name: string;
  trade: string;
  location: string;
  rating: number;
  reviewCount: number;
  responseTime: string;
  tier: ProviderTier;
  avatar: string;
  tagline: string;
  estimatedQuote: string;
  verified: boolean;
  yearsActive: number;
  completedJobs: number;
}

// ─── Tier config ──────────────────────────────────────────────────────────────

const TIER_CONFIG: Record<ProviderTier, { label: string; color: string; bg: string; icon: React.ElementType; contactLabel: string; contactDesc: string }> = {
  instant: {
    label: 'Instant Quote',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200',
    icon: Zap,
    contactLabel: 'Get Instant Quote',
    contactDesc: 'Auto-generates a detailed quote right now',
  },
  pro: {
    label: 'Pro',
    color: 'text-love-plum-light',
    bg: 'bg-purple-50 border-purple-200',
    icon: Crown,
    contactLabel: 'WhatsApp / Email',
    contactDesc: 'Sends your job details via WhatsApp & email',
  },
  basic: {
    label: 'Standard',
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200',
    icon: Mail,
    contactLabel: 'Send Enquiry',
    contactDesc: 'Sends an email with your job brief',
  },
};

// ─── Budget → min/max mapper ──────────────────────────────────────────────────

function parseBudget(value: string): { budget_min: number | null; budget_max: number | null } {
  const map: Record<string, { budget_min: number | null; budget_max: number | null }> = {
    'under-200':    { budget_min: 0,     budget_max: 200   },
    '200-500':      { budget_min: 200,   budget_max: 500   },
    '500-1000':     { budget_min: 500,   budget_max: 1000  },
    '1000-3000':    { budget_min: 1000,  budget_max: 3000  },
    '3000-10000':   { budget_min: 3000,  budget_max: 10000 },
    '10000-plus':   { budget_min: 10000, budget_max: null  },
    'unsure':       { budget_min: null,  budget_max: null  },
  };
  return map[value] ?? { budget_min: null, budget_max: null };
}

// ─── Urgency mapper ───────────────────────────────────────────────────────────

function mapUrgency(value: string): 'emergency' | 'high' | 'medium' | 'low' {
  const map: Record<string, 'emergency' | 'high' | 'medium' | 'low'> = {
    'emergency':  'emergency',
    'this-week':  'high',
    'this-month': 'medium',
    'flexible':   'low',
  };
  return map[value] ?? 'medium';
}

// ─── Subscription tier → ProviderTier ────────────────────────────────────────

function mapSubscriptionTier(tier: string): ProviderTier {
  if (tier === 'premium') return 'instant';
  if (tier === 'pro') return 'pro';
  return 'basic';
}

// ─── Response time by tier ────────────────────────────────────────────────────

function responseTimeByTier(tier: ProviderTier): string {
  if (tier === 'instant') return 'Instant quote';
  if (tier === 'pro') return 'Replies in ~15 min';
  return 'Replies in ~2 hrs';
}

// ─── Extract postcode area prefix ────────────────────────────────────────────

function extractPostcodeArea(postcode: string): string {
  // Extract the outward code prefix e.g. "LS1" from "LS1 2AB" or "Leeds" → just use as-is for text match
  const cleaned = postcode.trim().toUpperCase();
  // Match UK postcode outward code (e.g. LS1, SW1A, M1)
  const match = cleaned.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)/);
  return match ? match[1] : cleaned;
}

// ─── Real matching algorithm ──────────────────────────────────────────────────

async function matchProviders(
  tradeCategory: string,
  locationInput: string,
): Promise<MatchedProvider[]> {
  const supabase = createClient();
  const postcodeArea = extractPostcodeArea(locationInput);

  // Strategy 1: Query claimed provider_profiles joined with scraped_listings
  // Match on trade_category (case-insensitive) and postcode area prefix
  const { data: profileData } = await supabase
    .from('provider_profiles')
    .select(`
      id,
      business_name,
      trade_category,
      postcode,
      bio,
      subscription_tier,
      service_radius_km,
      certifications,
      is_featured,
      listing_id,
      scraped_listings (
        id,
        business_name,
        category,
        postcode,
        rating,
        address
      )
    `)
    .ilike('trade_category', `%${tradeCategory || ''}%`)
    .limit(20);

  const profileMatches: MatchedProvider[] = [];

  if (profileData && profileData.length > 0) {
    for (const profile of profileData) {
      // Filter by postcode proximity — check if provider postcode starts with same area
      const providerPostcode = extractPostcodeArea(profile.postcode || '');
      const locationLower = locationInput.toLowerCase();
      const providerPostcodeLower = (profile.postcode || '').toLowerCase();

      // Accept if postcode area matches OR location text appears in provider postcode/address
      const listing = Array.isArray(profile.scraped_listings)
        ? profile.scraped_listings[0]
        : profile.scraped_listings as { id: string; business_name: string; category: string; postcode: string; rating: number | null; address: string } | null;

      const listingAddress = (listing?.address || '').toLowerCase();
      const listingPostcode = extractPostcodeArea(listing?.postcode || '');

      const postcodeMatch =
        providerPostcode === postcodeArea ||
        providerPostcodeLower.startsWith(postcodeArea.toLowerCase()) ||
        listingPostcode === postcodeArea ||
        listingAddress.includes(locationLower) ||
        providerPostcodeLower.includes(locationLower);

      if (!postcodeMatch && locationInput.length > 2) continue;

      const tier = mapSubscriptionTier(profile.subscription_tier || 'free');
      const name = profile.business_name || listing?.business_name || 'Local Provider';
      const trade = profile.trade_category || listing?.category || tradeCategory || 'Tradesperson';
      const postcode = profile.postcode || listing?.postcode || locationInput;
      const rating = listing?.rating ? Number(listing.rating) : 4.5;
      const initials = name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

      profileMatches.push({
        id: profile.id,
        name,
        trade,
        location: postcode,
        rating,
        reviewCount: Math.floor(rating * 20 + 10),
        responseTime: responseTimeByTier(tier),
        tier,
        avatar: initials,
        tagline: profile.bio || `${trade} serving ${postcode} and surrounding areas.`,
        estimatedQuote: 'Quote on request',
        verified: (profile.certifications || []).length > 0 || profile.is_featured,
        yearsActive: 0,
        completedJobs: 0,
      });
    }
  }

  // Strategy 2: If not enough profile matches, fall back to scraped_listings
  if (profileMatches.length < 3) {
    const { data: listingData } = await supabase
      .from('scraped_listings')
      .select('id, business_name, category, postcode, rating, address, is_claimed')
      .ilike('category', `%${tradeCategory || ''}%`)
      .order('rating', { ascending: false })
      .limit(30);

    if (listingData) {
      const existingIds = new Set(profileMatches.map((p) => p.id));

      for (const listing of listingData) {
        if (existingIds.has(listing.id)) continue;

        const listingPostcode = extractPostcodeArea(listing.postcode || '');
        const listingAddressLower = (listing.address || '').toLowerCase();
        const locationLower = locationInput.toLowerCase();

        const postcodeMatch =
          listingPostcode === postcodeArea ||
          (listing.postcode || '').toLowerCase().startsWith(postcodeArea.toLowerCase()) ||
          listingAddressLower.includes(locationLower) ||
          (listing.postcode || '').toLowerCase().includes(locationLower);

        if (!postcodeMatch && locationInput.length > 2) continue;

        const name = listing.business_name || 'Local Provider';
        const trade = listing.category || tradeCategory || 'Tradesperson';
        const rating = listing.rating ? Number(listing.rating) : 4.3;
        const initials = name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

        profileMatches.push({
          id: listing.id,
          name,
          trade,
          location: listing.postcode || locationInput,
          rating,
          reviewCount: Math.floor(rating * 15 + 5),
          responseTime: 'Replies in ~2 hrs',
          tier: 'basic',
          avatar: initials,
          tagline: `${trade} based in ${listing.postcode || locationInput}.`,
          estimatedQuote: 'Quote on request',
          verified: listing.is_claimed,
          yearsActive: 0,
          completedJobs: 0,
        });

        if (profileMatches.length >= 12) break;
      }
    }
  }

  // Sort: featured/premium first, then by rating
  return profileMatches.sort((a, b) => {
    const tierOrder: ProviderTier[] = ['instant', 'pro', 'basic'];
    const tierDiff = tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier);
    if (tierDiff !== 0) return tierDiff;
    return b.rating - a.rating;
  });
}

// ─── Budget options ───────────────────────────────────────────────────────────

const BUDGET_OPTIONS = [
  { value: 'under-200', label: 'Under £200' },
  { value: '200-500', label: '£200 – £500' },
  { value: '500-1000', label: '£500 – £1,000' },
  { value: '1000-3000', label: '£1,000 – £3,000' },
  { value: '3000-10000', label: '£3,000 – £10,000' },
  { value: '10000-plus', label: '£10,000+' },
  { value: 'unsure', label: 'Not sure yet' },
];

const URGENCY_OPTIONS = [
  { value: 'emergency', label: '🚨 Emergency — today', desc: 'Needs doing ASAP' },
  { value: 'this-week', label: '⚡ This week', desc: 'Within the next 7 days' },
  { value: 'this-month', label: '📅 This month', desc: 'Within the next 30 days' },
  { value: 'flexible', label: '🕐 Flexible', desc: 'No rush, best price wins' },
];

// ─── Provider Card ────────────────────────────────────────────────────────────

interface ProviderCardProps {
  provider: MatchedProvider;
  highlighted: boolean;
  onToggleHighlight: (id: string) => void;
}

function ProviderCard({ provider, highlighted, onToggleHighlight }: ProviderCardProps) {
  const tier = TIER_CONFIG[provider.tier];
  const TierIcon = tier.icon;

  return (
    <div
      className={`relative rounded-2xl border-2 transition-all duration-200 bg-white overflow-hidden ${
        highlighted
          ? 'border-love-mint shadow-glow ring-2 ring-love-mint/30'
          : 'border-gray-100 shadow-card hover:shadow-card-hover hover:border-gray-200'
      }`}
    >
      {/* Tier badge */}
      <div className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${tier.bg} ${tier.color}`}>
        <TierIcon size={11} />
        {tier.label}
      </div>

      <div className="p-5">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-xl bg-gradient-plum flex items-center justify-center text-white font-bold text-sm shrink-0">
            {provider.avatar}
          </div>

          <div className="flex-1 min-w-0 pr-16">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{provider.name}</h3>
              {provider.verified && (
                <BadgeCheck size={14} className="text-love-plum-light shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{provider.trade} · {provider.location}</p>
            <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{provider.tagline}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Star size={11} className="text-amber-400 fill-amber-400" />
            <span className="font-semibold text-gray-700">{provider.rating.toFixed(1)}</span>
            <span>({provider.reviewCount})</span>
          </span>
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {provider.responseTime}
          </span>
          {provider.completedJobs > 0 && (
            <span className="flex items-center gap-1">
              <CheckCircle2 size={11} className="text-emerald-500" />
              {provider.completedJobs} jobs
            </span>
          )}
        </div>

        {/* Estimated quote */}
        <div className="mt-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400">Estimated quote</p>
            <p className="text-base font-bold text-love-plum-light">{provider.estimatedQuote}</p>
          </div>

          {/* Highlight toggle */}
          <button
            onClick={() => onToggleHighlight(provider.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
              highlighted
                ? 'bg-love-mint/20 text-emerald-700 border border-love-mint' :'bg-gray-50 text-gray-500 border border-gray-200 hover:border-love-mint hover:text-emerald-700'
            }`}
          >
            <Heart size={12} className={highlighted ? 'fill-emerald-600' : ''} />
            {highlighted ? 'Shortlisted' : 'Shortlist'}
          </button>
        </div>
      </div>

      {/* Contact method hint */}
      <div className="px-5 py-2.5 border-t border-gray-50 flex items-center gap-2 text-xs text-gray-400">
        <TierIcon size={11} className={tier.color} />
        <span>{tier.contactDesc}</span>
      </div>
    </div>
  );
}

// ─── Bulk Contact Modal ───────────────────────────────────────────────────────

interface BulkContactModalProps {
  providers: MatchedProvider[];
  jobTitle: string;
  onClose: () => void;
}

function BulkContactModal({ providers, jobTitle, onClose }: BulkContactModalProps) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const instantProviders = providers.filter((p) => p.tier === 'instant');
  const proProviders = providers.filter((p) => p.tier === 'pro');
  const basicProviders = providers.filter((p) => p.tier === 'basic');

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
    }, 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-plum-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900 font-display">Contact {providers.length} provider{providers.length !== 1 ? 's' : ''}</h2>
            <p className="text-xs text-gray-500 mt-0.5">One click — we handle the rest</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg font-display mb-2">Sent! 🎉</h3>
            <p className="text-sm text-gray-500 mb-6">
              Your job brief has been sent to all {providers.length} providers. Expect responses within their listed response times.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-love-plum-light text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Back to quotes
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Job summary */}
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Your job</p>
              <p className="text-sm font-medium text-gray-800">{jobTitle || 'Your job description'}</p>
            </div>

            {/* What happens per tier */}
            <div className="space-y-2">
              {instantProviders.length > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <Zap size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-emerald-700">{instantProviders.length} Instant Quote provider{instantProviders.length > 1 ? 's' : ''}</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Auto-generates a detailed quote immediately — no waiting</p>
                    <p className="text-xs text-gray-500 mt-1">{instantProviders.map((p) => p.name).join(', ')}</p>
                  </div>
                </div>
              )}
              {proProviders.length > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50 border border-purple-100">
                  <Crown size={16} className="text-love-plum-light mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-love-plum-light">{proProviders.length} Pro provider{proProviders.length > 1 ? 's' : ''}</p>
                    <p className="text-xs text-purple-600 mt-0.5">Sends your job brief via WhatsApp + email for fast response</p>
                    <p className="text-xs text-gray-500 mt-1">{proProviders.map((p) => p.name).join(', ')}</p>
                  </div>
                </div>
              )}
              {basicProviders.length > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <Mail size={16} className="text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-gray-600">{basicProviders.length} Standard provider{basicProviders.length > 1 ? 's' : ''}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Sends an email enquiry with your job details</p>
                    <p className="text-xs text-gray-400 mt-1">{basicProviders.map((p) => p.name).join(', ')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Additional detail note */}
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
              <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                Pro and Standard providers may ask a couple of follow-up questions before sending a final quote. Instant Quote providers generate one right away.
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-plum text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {sending ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Sending to all providers…
                </>
              ) : (
                <>
                  <Send size={15} />
                  Send to all {providers.length} providers
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Lazy Signup Gate Modal ───────────────────────────────────────────────────

interface SignupGateModalProps {
  onSuccess: () => void;
  onClose: () => void;
}

function SignupGateModal({ onSuccess, onClose }: SignupGateModalProps) {
  const { signUp, signIn } = useAuth();
  const [mode, setMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUp(email, password, { accountType: 'homeowner' });
      } else {
        await signIn(email, password);
      }
      onSuccess();
    } catch (err: any) {
      // If signup fails because user exists, nudge to sign in
      if (err?.message?.toLowerCase().includes('already registered') || err?.message?.toLowerCase().includes('user already exists')) {
        setMode('signin');
        setError('An account with this email already exists — sign in below.');
      } else {
        setError(err?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-plum-lg w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-plum flex items-center justify-center">
              <Lock size={14} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm font-display">Save your shortlist</h2>
              <p className="text-xs text-gray-400 mt-0.5">Quick — just email and password</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5">
          {/* Value prop */}
          <div className="mb-4 p-3 rounded-xl bg-purple-50 border border-purple-100">
            <p className="text-xs text-love-plum-light font-medium">
              ✓ Send your job to all shortlisted providers in one click
            </p>
            <p className="text-xs text-purple-500 mt-1">
              ✓ Track responses and quotes in one place
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full text-sm text-gray-800 border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Choose a password (6+ chars)' : 'Your password'}
                className="w-full text-sm text-gray-800 border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <AlertCircle size={13} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-plum text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                </>
              ) : (
                mode === 'signup' ? 'Create account & continue' : 'Sign in & continue'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-3">
            {mode === 'signup' ? (
              <>Already have an account?{' '}
                <button onClick={() => { setMode('signin'); setError(null); }} className="text-love-plum-light font-medium hover:underline">Sign in</button>
              </>
            ) : (
              <>No account yet?{' '}
                <button onClick={() => { setMode('signup'); setError(null); }} className="text-love-plum-light font-medium hover:underline">Sign up free</button>
              </>
            )}
          </p>

          <p className="text-center text-xs text-gray-300 mt-2">No verification email. No spam. Just quotes.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Inference helper ─────────────────────────────────────────────────────────

/**
 * Infer trade service value from a free-text query.
 * Checks if any known service label appears (case-insensitive) in the query.
 */
function inferService(query: string, allServices: { value: string; label: string }[]): string {
  const q = query.toLowerCase();
  // keyword → service value mappings for common shorthand
  const KEYWORD_MAP: Record<string, string> = {
    plumber: 'Plumber',
    plumbing: 'Plumber',
    electrician: 'Electrician',
    electrical: 'Electrician',
    electric: 'Electrician',
    boiler: 'Boiler Engineer',
    'gas engineer': 'Gas Engineer',
    'gas safe': 'Gas Engineer',
    roofer: 'Roofer',
    roofing: 'Roofer',
    roof: 'Roofer',
    carpenter: 'Carpenter',
    carpentry: 'Carpenter',
    joiner: 'Joiner',
    painter: 'Painter & Decorator',
    decorator: 'Painter & Decorator',
    decorating: 'Painter & Decorator',
    plasterer: 'Plasterer',
    plastering: 'Plasterer',
    tiler: 'Tiler',
    tiling: 'Tiler',
    landscaper: 'Landscaper',
    landscaping: 'Landscaper',
    gardener: 'Gardener',
    garden: 'Gardener',
    handyman: 'Handyman',
    builder: 'Builder',
    'kitchen fitter': 'Kitchen Fitter',
    kitchen: 'Kitchen Fitter',
    'bathroom fitter': 'Bathroom Fitter',
    bathroom: 'Bathroom Fitter',
    flooring: 'Flooring Specialist',
    carpet: 'Carpet Fitter',
    fencer: 'Fencer',
    fence: 'Fencer',
    patio: 'Patio & Driveway Specialist',
    driveway: 'Patio & Driveway Specialist',
    locksmith: 'Locksmith',
    cleaner: 'Cleaner',
    cleaning: 'Cleaner',
    'window cleaner': 'Window Cleaner',
    window: 'Window Fitter',
    glazier: 'Glazier',
    scaffolder: 'Scaffolder',
    bricklayer: 'Bricklayer',
    brickwork: 'Bricklayer',
    loft: 'Loft Conversion Specialist',
    extension: 'Extension Specialist',
    'pest control': 'Pest Control Specialist',
    pest: 'Pest Control Specialist',
    'tree surgeon': 'Tree Surgeon',
    tree: 'Tree Surgeon',
    'solar panel': 'Solar Panel Installer',
    solar: 'Solar Panel Installer',
  };

  // Check keyword map first (longest match wins)
  const sortedKeys = Object.keys(KEYWORD_MAP).sort((a, b) => b.length - a.length);
  for (const kw of sortedKeys) {
    if (q.includes(kw)) {
      const matched = allServices.find((s) => s.value === KEYWORD_MAP[kw]);
      if (matched) return matched.value;
    }
  }

  // Fallback: check service labels directly
  for (const svc of allServices) {
    if (q.includes(svc.label.toLowerCase())) return svc.value;
  }

  return '';
}

/**
 * Extract a UK location from a free-text query.
 * Looks for patterns like "in <Location>", "near <Location>", "at <Location>".
 */
function inferLocation(query: string): string {
  const match = query.match(/\b(?:in|near|at|around)\s+([A-Za-z][A-Za-z\s]{1,30}?)(?:\s*[,.]|$|\s+(?:for|to|–|-|,|budget|urgently|asap|today|this))/i);
  if (match) return match[1].trim();
  return '';
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function PostJobContent() {
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('describe');
  const [isSearching, setIsSearching] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state
  const [jobDescription, setJobDescription] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [location, setLocation] = useState('');
  const [budget, setBudget] = useState('');
  const [urgency, setUrgency] = useState('');
  const [extraNotes, setExtraNotes] = useState('');

  // Quotes state
  const [matchedProviders, setMatchedProviders] = useState<MatchedProvider[]>([]);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [showSignupGate, setShowSignupGate] = useState(false);
  const [sortBy, setSortBy] = useState<'rating' | 'price' | 'speed'>('rating');

  // All services flat list for dropdown
  const allServices = TRADE_SUPER_CATEGORIES.flatMap((s) =>
    s.categories.flatMap((c) => c.services)
  ).sort((a, b) => a.label.localeCompare(b.label));

  // Pre-fill from hero search query
  useEffect(() => {
    const q = searchParams.get('q');
    if (!q) return;
    setJobDescription(q);
    const inferredService = inferService(q, allServices);
    if (inferredService) setSelectedService(inferredService);
    const inferredLocation = inferLocation(q);
    if (inferredLocation) setLocation(inferredLocation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleHighlight = (id: string) => {
    setHighlighted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const highlightedProviders = matchedProviders.filter((p) => highlighted.has(p.id));

  const sortedProviders = [...matchedProviders].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'speed') {
      const order: ProviderTier[] = ['instant', 'pro', 'basic'];
      return order.indexOf(a.tier) - order.indexOf(b.tier);
    }
    // price: sort by tier as proxy (no real price data yet)
    const tierOrder: ProviderTier[] = ['basic', 'pro', 'instant'];
    return tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier);
  });

  const canSubmit = jobDescription.trim().length > 10 && location.trim().length > 2 && budget;

  const handleFindQuotes = async () => {
    if (!canSubmit) return;
    setIsSearching(true);
    setSubmitError(null);

    try {
      const supabase = createClient();
      const { budget_min, budget_max } = parseBudget(budget);
      const titleParts = [selectedService || 'Trade job', 'needed in', location].filter(Boolean);
      const title = titleParts.join(' ');
      const fullDescription = [jobDescription, extraNotes ? `Additional notes: ${extraNotes}` : ''].filter(Boolean).join('\n\n');

      const { error } = await supabase.from('job_posts').insert({
        title,
        description: fullDescription || null,
        category: selectedService || 'General',
        postcode: location,
        urgency: mapUrgency(urgency),
        budget_min,
        budget_max,
      });

      if (error) {
        console.error('Failed to save job:', error.message);
        setSubmitError('We could not save your job right now. Please try again.');
        setIsSearching(false);
        return;
      }

      // Run real matching algorithm
      const providers = await matchProviders(selectedService, location);
      setMatchedProviders(providers);
      setHighlighted(new Set());
    } catch (err) {
      console.error('Unexpected error:', err);
      setSubmitError('Something went wrong. Please try again.');
      setIsSearching(false);
      return;
    }

    setIsSearching(false);
    setStep('quotes');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactAll = () => {
    if (!user) {
      setShowSignupGate(true);
    } else {
      setShowModal(true);
    }
  };

  return (
    <main className="min-h-screen" style={{ backgroundColor: '#F8F5FA' }}>
      <Navbar />

      {/* ── Page header ── */}
      <section className="pt-24 pb-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link href="/homepage" className="hover:text-love-plum-light transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-gray-600 font-medium">Post a Job</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-8">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              step === 'describe' ? 'bg-love-plum-light text-white' : 'bg-emerald-100 text-emerald-700'
            }`}>
              {step === 'quotes' ? <CheckCircle2 size={13} /> : <span>1</span>}
              Describe your job
            </div>
            <div className="w-8 h-px bg-gray-300" />
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              step === 'quotes' ? 'bg-love-plum-light text-white' : 'bg-gray-100 text-gray-400'
            }`}>
              <span>2</span>
              Matched quotes
            </div>
          </div>

          {step === 'describe' ? (
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display mb-2">
                Tell us what you need done
              </h1>
              <p className="text-gray-500 text-sm">
                Describe your job and we'll match you with local pros who can quote right now.
              </p>
            </div>
          ) : (
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display mb-2">
                {matchedProviders.length > 0
                  ? `${matchedProviders.length} provider${matchedProviders.length !== 1 ? 's' : ''} matched your job`
                  : 'No providers found nearby'}
              </h1>
              <p className="text-gray-500 text-sm">
                {matchedProviders.length > 0
                  ? 'Shortlist the ones you like, then contact them all with one click.' :'Try broadening your location or trade type to find more providers.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Step 1: Describe ── */}
      {step === 'describe' && (
        <section className="pb-20 px-4">
          <div className="max-w-3xl mx-auto space-y-5">

            {/* Job description */}
            <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                What do you need doing? <span className="text-love-coral">*</span>
              </label>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="e.g. My boiler stopped working overnight. It's a Worcester Bosch combi, about 8 years old. Need a Gas Safe engineer to diagnose and fix it — ideally today or tomorrow."
                rows={4}
                className="w-full text-sm text-gray-800 placeholder-gray-400 border border-gray-200 rounded-xl px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all"
              />
              <p className="text-xs text-gray-400 mt-1.5">The more detail you give, the more accurate the quotes.</p>
            </div>

            {/* Trade type */}
            <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Trade / service type
              </label>
              <div className="relative">
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full text-sm text-gray-700 border border-gray-200 rounded-xl px-4 py-3 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all bg-white"
                >
                  <option value="">Select a trade (optional — we can infer from description)</option>
                  {allServices.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Location + Budget row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Location <span className="text-love-coral">*</span>
                </label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Town, city or postcode"
                    className="w-full text-sm text-gray-700 border border-gray-200 rounded-xl pl-9 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all"
                  />
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Budget <span className="text-love-coral">*</span>
                </label>
                <div className="relative">
                  <PoundSterling size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="w-full text-sm text-gray-700 border border-gray-200 rounded-xl pl-9 pr-10 py-3 appearance-none focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all bg-white"
                  >
                    <option value="">Select budget range</option>
                    {BUDGET_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Urgency */}
            <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                How urgent is this?
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {URGENCY_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setUrgency(o.value)}
                    className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all duration-150 ${
                      urgency === o.value
                        ? 'border-love-plum-light bg-purple-50 ring-2 ring-love-plum-light/20' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-800 leading-tight">{o.label}</span>
                    <span className="text-xs text-gray-400 mt-1">{o.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Extra notes */}
            <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Anything else providers should know? <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={extraNotes}
                onChange={(e) => setExtraNotes(e.target.value)}
                placeholder="e.g. Parking available, access via side gate, prefer morning appointments, materials already purchased…"
                rows={3}
                className="w-full text-sm text-gray-800 placeholder-gray-400 border border-gray-200 rounded-xl px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-love-plum-light/30 focus:border-love-plum-light transition-all"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleFindQuotes}
              disabled={!canSubmit || isSearching}
              className="w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl bg-gradient-plum text-white font-bold text-base shadow-plum-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99]"
            >
              {isSearching ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Matching you with local pros…
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Find matched quotes
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            {!canSubmit && (
              <p className="text-center text-xs text-gray-400">
                Fill in your job description, location, and budget to continue.
              </p>
            )}

            {submitError && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <AlertCircle size={14} className="text-red-500 shrink-0" />
                <p className="text-xs text-red-700">{submitError}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Step 2: Quotes ── */}
      {step === 'quotes' && (
        <section className="pb-24 px-4">
          <div className="max-w-3xl mx-auto">

            {/* Sort + filter bar */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={14} className="text-gray-400" />
                <span className="text-xs text-gray-500 font-medium">Sort by:</span>
                {(['rating', 'price', 'speed'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      sortBy === s
                        ? 'bg-love-plum-light text-white' :'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {s === 'rating' ? '⭐ Rating' : s === 'price' ? '💷 Price' : '⚡ Speed'}
                  </button>
                ))}
              </div>

              {/* Edit job */}
              <button
                onClick={() => setStep('describe')}
                className="text-xs text-love-plum-light font-medium hover:underline"
              >
                ← Edit job details
              </button>
            </div>

            {/* Provider grid */}
            {sortedProviders.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {sortedProviders.map((provider) => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    highlighted={highlighted.has(provider.id)}
                    onToggleHighlight={toggleHighlight}
                  />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <MapPin size={28} className="text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm font-medium mb-1">No providers found in this area</p>
                <p className="text-gray-400 text-xs mb-4">Try a different location or trade type.</p>
                <button
                  onClick={() => setStep('describe')}
                  className="px-5 py-2.5 rounded-xl bg-love-plum-light text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  Edit job details
                </button>
              </div>
            )}

            {/* Shortlist CTA bar */}
            <div
              className={`sticky bottom-4 transition-all duration-300 ${
                highlighted.size > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
              }`}
            >
              <div className="bg-gradient-plum rounded-2xl shadow-plum-lg p-4 flex items-center justify-between gap-4">
                <div className="text-white">
                  <p className="font-bold text-sm font-display">
                    {highlighted.size} provider{highlighted.size !== 1 ? 's' : ''} shortlisted
                  </p>
                  <p className="text-xs text-white/70 mt-0.5">
                    Mix of {[...new Set(highlightedProviders.map((p) => p.tier))].join(', ')} tiers
                  </p>
                </div>
                <button
                  onClick={handleContactAll}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-love-mint text-love-plum font-bold text-sm hover:bg-love-mint-bright transition-colors shrink-0"
                >
                  <Send size={14} />
                  Contact all
                </button>
              </div>
            </div>

            {/* Contact all (always visible fallback) */}
            {highlighted.size === 0 && sortedProviders.length > 0 && (
              <div className="mt-2 p-4 rounded-2xl bg-white border border-gray-100 shadow-card text-center">
                <Heart size={20} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Shortlist providers you like, then contact them all at once.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Bulk contact modal ── */}
      {showModal && (
        <BulkContactModal
          providers={highlightedProviders}
          jobTitle={jobDescription}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* ── Lazy signup gate ── */}
      {showSignupGate && (
        <SignupGateModal
          onSuccess={() => {
            setShowSignupGate(false);
            setShowModal(true);
          }}
          onClose={() => setShowSignupGate(false)}
        />
      )}
    </main>
  );
}

export default function PostJobPage() {
  return (
    <Suspense fallback={null}>
      <PostJobContent />
    </Suspense>
  );
}
